import React, { HTMLAttributes } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import Portal from "component/atoms/portal";
import Thumbnail from "component/atoms/thumbnail";
import { ContextMenu, MenuItem, ContextMenuTrigger } from "react-contextmenu";
import { thumbnailDragHandlers } from "./ThumbnailDragHandlers";
import Cache from "core/cache";
import SVGIcons from "assets/icon";
import Helper from "helper/index";
import "./ThumbnailEntry.styl";
import "./react-contextmenu.styl";
/**
 * @description ThumbnailEntry UI Component Properties
 *
 * @interface ILayoutChooserProps
 */
interface IThumbnailEntryProps {
  /**
   * @description 레이아웃 선택을 위한 표의 행 갯수
   */
  index: number;
  thumbnailFrame: number;
  metadataSpec: any;
  isDragAndDrop?: boolean;
  active?: boolean;
  numberOfFrames?: number;
  onClick: any;
  onDblClick: any;
  onClear: any;
  onDelete: any;
}

/**
 * @description thumbnailEntry UI Component Properties
 *
 * @interface IthumbnailEntryStates
 * @extends {HTMLAttributes<HTMLDivElement>}
 */
interface IthumbnailEntryStates {
  /**
   * @description 현재 확장 UI 열림/닫힘 상태
   */
  isDrag: boolean;
  isRemoved: boolean;
}

/**
 *
 *
 * @class ThumbnailEntry
 * @extends {React.Component<IThumbnailEntryProps, IthumbnailEntryStates>}
 */
class ThumbnailEntry extends React.Component<
  IThumbnailEntryProps,
  IthumbnailEntryStates
> {
  private clickTimer: any = null;
  private timeForSingleClick = 180;
  private isMouseDragging = false;

  /**
   *
   *
   * @static
   * @memberof ThumbnailEntry
   */
  static defaultProps = {
    id: 0,
    studyDescription: "",
    seriesDescription: "",
    seriesNumber: undefined,
    instanceNumber: undefined,
    numberOfFrames: 0,
    thumbnailFrame: 0,
    isDragAndDrop: true
  };

  /**
   *Creates an instance of ThumbnailEntry.
   * @param {*} props
   * @memberof ThumbnailEntry
   */
  constructor(props: any) {
    super(props);
    this.state = {
      isDrag: false,
      isRemoved: false
    };
  }
  /**
   *
   *
   * @memberof ThumbnailEntry
   */
  componentDidMount() {
    const thumbnailElement = ReactDOM.findDOMNode(this);

    if (thumbnailElement === null || thumbnailElement === undefined) {
      return;
    }

    let childs = (thumbnailElement as any).children[0] as any;
    const viewport = Helper.DomHelper.getSiblings(childs, ".thumbnailEntry")[0];

    viewport.addEventListener("mousedown", this.handleMouseDown);
    viewport.addEventListener("click", this.handleMouseClick);
    viewport.addEventListener(
      "touchstart",
      this.handleTouchStart,
      Helper.Browser.APITest.isSupportPassiveListener()
        ? { passive: true }
        : false
    );
    viewport.addEventListener(
      "touchmove",
      this.handleTouchMove,
      Helper.Browser.APITest.isSupportPassiveListener()
        ? { passive: true }
        : false
    );
    viewport.addEventListener("touchend", this.handleTouchEnd);
  }

  /**
   *
   *
   * @memberof ThumbnailEntry
   */
  componentWillUnmount() {
    const thumbnailElement = ReactDOM.findDOMNode(this);
    let childs = (thumbnailElement as any).children[0] as any;
    const viewport = Helper.DomHelper.getSiblings(childs, ".thumbnailEntry")[0];

    viewport.removeEventListener("mousedown", this.handleMouseDown);
    viewport.removeEventListener("click", this.handleMouseClick);
    viewport.removeEventListener("touchstart", this.handleTouchStart);
    viewport.removeEventListener("touchmove", this.handleTouchMove);
    viewport.removeEventListener("touchend", this.handleTouchEnd);
  }

  /**
   *
   *
   * @memberof ThumbnailEntry
   */
  handleMouseDown = (event: any) => {
    const { metadataSpec } = this.props;
    const metadataID = {
      seriesInstanceUID: metadataSpec.seriesInstanceUID,
      modality: metadataSpec.modality
    };
    if (event.button !== 0) return;
    // this.setActive();
    // 마우스 드래그 상태 추적을 위해 아래와 같이 수정하였는데,
    // 이부분에 관해선 추후 적절한 방법으로 수정 필요
    thumbnailDragHandlers.thumbnailDragStartHandler(event, metadataID, this);
  };

  /**
   *
   *
   * @memberof ThumbnailEntry
   */

  handleMouseClick = (event: any) => {
    event = window.event || event;
    if (this.clickTimer) {
      window.clearTimeout(this.clickTimer);
      this.clickTimer = null;

      if (this.isMouseDragging) {
        this.isMouseDragging = false;
        return;
      }

      /// TODO : 더블 클릭시 수행되어야할 이벤트 처리 작성
      this.handleDoubleClick(event);
    } else {
      this.clickTimer = window.setTimeout(
        function(this: ThumbnailEntry) {
          // ref : https://stackoverflow.com/questions/55152179/typescript-reactjs-settimeout-this-implicitly-has-type-any-because-it-d
          // tslint:disable-next-line:no-shadowed-variable
          const that: any = this;
          clearTimeout(that.clickTimer as any);
          that.clickTimer = null;

          if (this.isMouseDragging) {
            this.isMouseDragging = false;
            return;
          }

          /// TODO : 단일 클릭시 수행되어야할 이벤트 처리 작성
          that.handleMouseSingleClick(event);
        }.bind(this),
        this.timeForSingleClick
      );
    }
  };

  handleMouseSingleClick = (event: any) => {
    const { metadataSpec, onClick } = this.props;
    const metadataID = Cache.KeyGen.makeKey(
      metadataSpec.seriesInstanceUID,
      metadataSpec.modality
    );
    if (onClick) {
      onClick(event, metadataID);
    }
  };
  /**
   * @method handleDoubleClick
   * @param {Event} event
   * @memberof ThumbnailEntry
   */
  handleDoubleClick = (event: any) => {
    const { metadataSpec, onDblClick } = this.props;

    const metadataID = Cache.KeyGen.makeKey(
      metadataSpec.seriesInstanceUID,
      metadataSpec.modality
    );

    if (onDblClick) {
      onDblClick(event, metadataID);
    }
  };

  /**
   *
   *
   * @memberof ThumbnailEntry
   */
  handleTouchStart = (event: any) => {
    const { metadataSpec } = this.props;
    const itemSerieskey = {
      seriesInstanceUID: metadataSpec.seriesInstanceUID,
      modality: metadataSpec.modality
    };

    thumbnailDragHandlers.thumbnailDragStartHandler(event, itemSerieskey);
  };

  /**
   *
   *
   * @memberof ThumbnailEntry
   */
  handleTouchMove = (event: any) => {
    thumbnailDragHandlers.thumbnailDragHandler(event);
  };
  /**
   *
   *
   * @memberof ThumbnailEntry
   */
  handleTouchEnd = (event: any) => {
    const { metadataSpec } = this.props;
    const itemSerieskey = {
      seriesInstanceUID: metadataSpec.seriesInstanceUID,
      modality: metadataSpec.modality
    };
    thumbnailDragHandlers.thumbnailDragEndHandler(event, itemSerieskey);
  };

  /**
   *
   *
   * @memberof ThumbnailEntry
   */
  handleRemove = (event: any, data: any) => {
    const { onDelete, metadataSpec } = this.props;
    event.stopPropagation();
    if (typeof metadataSpec.seriesID === undefined) {
      return;
    }
    if (metadataSpec.seriesID === undefined || metadataSpec.seriesID === null) {
      return;
    }
    if (onDelete) {
      onDelete(metadataSpec.seriesID);
    }
  };

  /**
   *
   *
   * @memberof ThumbnailEntry
   */
  handleRemoveAll = (event: any, data: any) => {
    const { onClear, metadataSpec } = this.props;
    event.stopPropagation();
    if (typeof metadataSpec.seriesID === undefined) {
      return;
    }
    this.setState({ isRemoved: true });

    if (onClear) {
      onClear();
    }
  };
  /**
   *
   *
   * @returns
   * @memberof ThumbnailEntry
   */
  getDraggableClass() {
    const { isDragAndDrop } = this.props;
    return isDragAndDrop ? "draggable" : "";
  }

  /**
   *
   *
   * @returns
   * @memberof ThumbnailEntry
   */
  getThumbnailEntryClass() {
    let style = "thumbnailEntry noselect nodrag m-t-1 m-b-2 ";
    style += this.getDraggableClass();

    return style;
  }

  /**
   *
   *
   * @returns
   * @memberof ThumbnailEntry
   */
  getSeriesDetailClass() {
    const { metadataSpec } = this.props;
    let style = "seriesDetails flex-h m-x-1 + ";
    if (!metadataSpec.studyDescription) {
      style += "info-only";
    }

    return style;
  }
  /**
   *
   *
   * @returns
   * @memberof ThumbnailEntry
   */
  getContextMenuClassID() {
    const { metadataSpec } = this.props;
    return "context_" + metadataSpec.seriesInstanceUID;
  }

  /**
   *
   *
   * @returns
   * @memberof ThumbnailEntry
   */
  getActiveClass() {
    if (this.props.active === true) {
      return "active";
    } else {
      return "";
    }
  }

  /**
   *
   *
   * @returns
   * @memberof ThumbnailEntry
   */
  renderDescription() {
    const { metadataSpec } = this.props;
    let studyDescription = metadataSpec.studyDescription;
    if (metadataSpec.studyDescription === undefined) {
      studyDescription = "";
    }

    let seriesDescription = metadataSpec.seriesDescription;
    if (metadataSpec.seriesDescription === undefined) {
      seriesDescription = "";
    }

    const date = this.getDateString();
    let descriptionView = (
      <React.Fragment>
        <div className="seriesDescription flex-grow">
          <div>{studyDescription}</div>
          <div>{seriesDescription}</div>
        </div>
        <div className="seriesDate flex-grow">
          <div className="icon" />
          <div className="date-value">{date} </div>
        </div>
      </React.Fragment>
    );

    return descriptionView;
  }

  getDateString() {
    const { metadataSpec } = this.props;
    let date = metadataSpec.studyDate;
    let time = metadataSpec.studyTime;
    return date + " " + time;
  }

  /**
   *
   *
   * @returns
   * @memberof ThumbnailEntry
   */
  renderSeriesNumber() {
    let { metadataSpec } = this.props;
    if (metadataSpec.seriesNumber === undefined) {
      metadataSpec.seriesNumber = -1;
    }

    let seriesSeriesNumberView = (
      <div className="item item-series clearfix">
        <div className="icon">S:</div>
        <div className="value">{metadataSpec.seriesNumber}</div>
      </div>
    );

    return seriesSeriesNumberView;
  }

  /**
   *
   *
   * @returns
   * @memberof ThumbnailEntry
   */
  renderNumImageFrames() {
    let { numberOfFrames } = this.props;
    if (!numberOfFrames) {
      return;
    }

    let numImageFramesView = (
      <div className="item item-frames clearfix">
        <div className="icon">
          <SVGIcons.SVGLayerStackIcon />
        </div>
        <div className="value">{"  " + numberOfFrames}</div>
      </div>
    );

    return numImageFramesView;
  }

  /**
   *
   *
   * @returns
   * @memberof ThumbnailEntry
   */
  render() {
    let { metadataSpec, thumbnailFrame } = this.props;

    const classList = classNames(
      "thumbnailEntry",
      "noselect",
      "nodrag",
      this.getDraggableClass(),
      this.getActiveClass()
    );
    return (
      <React.Fragment>
        <ContextMenuTrigger
          id={this.getContextMenuClassID()}
          holdToDisplay={-1}
        >
          <div className={classList}>
            <div className="p-x-12">
              <Thumbnail
                inlist=""
                thumbnailFrame={thumbnailFrame}
                seriesID={metadataSpec.seriesID}
              />
            </div>
            <div className={this.getSeriesDetailClass()}>
              {this.renderDescription()}
              {/* {this.renderDate()} */}
              <div className="seriesInformation">
                {this.renderNumImageFrames()}
              </div>
            </div>
          </div>
        </ContextMenuTrigger>
        <Portal>
          <ContextMenu id={this.getContextMenuClassID()}>
            <MenuItem data={{ foo: "bar" }} onClick={this.handleRemove}>
              Remove it
            </MenuItem>
            <MenuItem data={{ foo: "bar" }} onClick={this.handleRemoveAll}>
              Remove all
            </MenuItem>
          </ContextMenu>
        </Portal>
      </React.Fragment>
    );
  }
}

export default ThumbnailEntry;
